import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { apiFetch, ApiCallError } from '../lib/api.ts';
import type { DispatchPlanSummary } from './DispatchListPage.tsx';

interface CartonGroup {
  pcsPerCarton: number;
  cartonTags: number;
}
interface Draft {
  sku: string;
  qtyOrdered: number;
  unitTags: number;
  cartons: CartonGroup[];
  declaredQty: number | null;
  isExcluded: boolean;
  recommendedCartonSize: number | null;
}
interface SubmitResult {
  doNo: string;
  linesPushed: number;
  planQty: number;
}

/**
 * Pieces this line accounts for. An untagged line has no tags to count, so it
 * is whatever the worker recorded.
 */
const piecesOf = (l: Draft): number =>
  l.isExcluded
    ? (l.declaredQty ?? 0)
    : l.unitTags + l.cartons.reduce((t, c) => t + c.pcsPerCarton * c.cartonTags, 0);

/** The split we suggested for this line, recomputed client-side for "reset". */
function recommendedFor(l: Draft): { unitTags: number; cartons: CartonGroup[] } {
  const size = l.recommendedCartonSize;
  if (size === null || size <= 1 || l.qtyOrdered < size) {
    return { unitTags: l.qtyOrdered, cartons: [] };
  }
  return {
    unitTags: l.qtyOrdered % size,
    cartons: [{ pcsPerCarton: size, cartonTags: Math.floor(l.qtyOrdered / size) }],
  };
}

export function DispatchKeyingPage() {
  const { doNo = '' } = useParams();
  const qc = useQueryClient();
  const path = `/api/dashboard/dispatch/${encodeURIComponent(doNo)}`;

  const q = useQuery({
    queryKey: ['dispatch-plan', doNo],
    queryFn: () => apiFetch<DispatchPlanSummary>(path),
  });

  const [lines, setLines] = useState<Draft[]>([]);
  const [note, setNote] = useState('');
  const [result, setResult] = useState<SubmitResult | null>(null);

  const fromServer = useMemo<Draft[]>(
    () =>
      (q.data?.lines ?? []).map((l) => ({
        sku: l.sku,
        qtyOrdered: l.qtyOrdered,
        unitTags: l.unitTags,
        cartons: l.cartons.map((c) => ({ ...c })),
        declaredQty: l.declaredQty ?? null,
        isExcluded: l.isExcluded,
        recommendedCartonSize: l.recommendedCartonSize,
      })),
    [q.data],
  );

  useEffect(() => {
    setLines(fromServer);
    setNote(q.data?.note ?? '');
  }, [fromServer, q.data]);

  // Nothing to save until something actually differs from what the server holds.
  const dirty = useMemo(
    () =>
      JSON.stringify(lines) !== JSON.stringify(fromServer) || note.trim() !== (q.data?.note ?? ''),
    [lines, fromServer, note, q.data],
  );

  const keyingBody = () => ({
    lines: lines.map((l) => ({
      sku: l.sku,
      unitTags: l.unitTags,
      cartons: l.cartons,
      declaredQty: l.declaredQty,
    })),
    note: note.trim() ? note.trim() : null,
  });

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['dispatch-plan', doNo] });
    void qc.invalidateQueries({ queryKey: ['dispatch-plans'] });
  };

  /** One save for the whole plan — tag split and recorded quantities alike. */
  const save = useMutation({
    mutationFn: () =>
      apiFetch(`${path}/keying`, { method: 'PUT', body: JSON.stringify(keyingBody()) }),
    onSuccess: refresh,
  });

  const submit = useMutation({
    mutationFn: async () => {
      await apiFetch(`${path}/keying`, { method: 'PUT', body: JSON.stringify(keyingBody()) });
      return apiFetch<SubmitResult>(`${path}/submit`, {
        method: 'POST',
        body: JSON.stringify({ note: note.trim() ? note.trim() : null }),
      });
    },
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
  });

  const reopen = useMutation({
    mutationFn: () => apiFetch(`${path}/reopen`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: () => apiFetch(`${path}/cancel`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const patch = (i: number, next: Partial<Draft>): void =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...next } : l)));

  if (q.isLoading) return <div className="loading">Loading…</div>;
  if (q.error) return <div className="error">{(q.error as Error).message}</div>;
  if (!q.data) return null;

  const plan = q.data;
  const isDraft = plan.status === 'draft';
  // Submitted means settled, for tags and recorded quantities alike: the
  // SmartLink ground order is out. Corrections go through re-open.
  const recordable = isDraft;
  const totalOrdered = lines.reduce((t, l) => t + l.qtyOrdered, 0);
  const totalPlanned = lines.reduce((t, l) => t + piecesOf(l), 0);
  const overPlanned = lines.some((l) => piecesOf(l) > l.qtyOrdered);
  const anyUntagged = lines.some((l) => l.isExcluded);
  const tagTotal = lines
    .filter((l) => !l.isExcluded)
    .reduce((t, l) => t + l.unitTags + l.cartons.reduce((c, g) => c + g.cartonTags, 0), 0);
  const busy = save.isPending || submit.isPending;
  const err = (submit.error ?? save.error ?? cancel.error ?? reopen.error) as
    | ApiCallError
    | Error
    | null;

  return (
    <>
      <Link to="/dispatch" className="back-link">
        ← Back to Dispatch
      </Link>
      <header className="main-header">
        <h1 className="main-title">
          Dispatch <span className="mono">{plan.doNo}</span>
        </h1>
        <span className="main-subtitle">
          <Link to={`/delivery-orders/${encodeURIComponent(plan.doNo)}`}>view the DO</Link>
          {' · '}
          <span className={`badge ${isDraft ? 'badge-pending' : 'badge-ok'}`}>{plan.status}</span>
        </span>
      </header>

      {result && (
        <div className="notice notice-ok">
          Submitted — {result.linesPushed} lines / {result.planQty} tags pushed to SmartLink.
          {result.linesPushed === 0 &&
            ' No RFID lines, so nothing was pushed; the recorded quantities still invoice.'}
        </div>
      )}
      {err && <div className="error">{err.message}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>SKU</th>
            <th className="num">Ordered</th>
            <th className="num">Individual tags</th>
            <th>Cartons (pcs × tags)</th>
            <th className="num">Shipping</th>
            <th className="num">Variance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const planned = piecesOf(l);
            const variance = planned - l.qtyOrdered;
            return (
              <tr key={l.sku}>
                <td className="mono">
                  {l.sku}
                  <div className="keying-summary">
                    {l.isExcluded
                      ? 'not RFID-tracked — recorded, never scanned'
                      : l.recommendedCartonSize
                        ? `normally packed ${l.recommendedCartonSize} per carton`
                        : 'no carton history — suggesting individual tags'}
                  </div>
                </td>
                <td className="num">{l.qtyOrdered}</td>
                <td className="num">
                  {l.isExcluded ? (
                    // No tags exist for this SKU, so the worker records pieces
                    // directly instead of a tag count.
                    <input
                      type="number"
                      min={0}
                      value={l.declaredQty ?? l.qtyOrdered}
                      disabled={!recordable}
                      onChange={(e) =>
                        patch(i, { declaredQty: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      min={0}
                      value={l.unitTags}
                      disabled={!isDraft}
                      onChange={(e) =>
                        patch(i, { unitTags: Math.max(0, Number(e.target.value) || 0) })
                      }
                    />
                  )}
                </td>
                <td>
                  {l.isExcluded ? (
                    <span className="keying-summary">—</span>
                  ) : (
                    <>
                      {l.cartons.map((c, ci) => (
                        <div key={ci} className="carton-row">
                          <input
                            type="number"
                            min={1}
                            value={c.pcsPerCarton}
                            disabled={!isDraft}
                            onChange={(e) =>
                              patch(i, {
                                cartons: l.cartons.map((x, j) =>
                                  j === ci ? { ...x, pcsPerCarton: Number(e.target.value) || 0 } : x,
                                ),
                              })
                            }
                          />
                          {' × '}
                          <input
                            type="number"
                            min={1}
                            value={c.cartonTags}
                            disabled={!isDraft}
                            onChange={(e) =>
                              patch(i, {
                                cartons: l.cartons.map((x, j) =>
                                  j === ci ? { ...x, cartonTags: Number(e.target.value) || 0 } : x,
                                ),
                              })
                            }
                          />
                          {' = '}
                          {c.pcsPerCarton * c.cartonTags} pcs
                          {isDraft && (
                            <button
                              type="button"
                              onClick={() =>
                                patch(i, { cartons: l.cartons.filter((_, j) => j !== ci) })
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      {isDraft && (
                        <div className="carton-row">
                          <button
                            type="button"
                            onClick={() =>
                              patch(i, {
                                cartons: [
                                  ...l.cartons,
                                  { pcsPerCarton: l.recommendedCartonSize ?? 1, cartonTags: 1 },
                                ],
                              })
                            }
                          >
                            Add carton size
                          </button>
                          <button type="button" onClick={() => patch(i, recommendedFor(l))}>
                            Reset to suggested
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </td>
                <td className="num">{planned}</td>
                <td className="num">
                  {variance === 0 ? '0' : `${variance > 0 ? '+' : ''}${variance}`}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="keying-summary">
        {totalPlanned} of {totalOrdered} pcs going out
        {tagTotal > 0 && ` · ${tagTotal} tags to scan at the ground stage`}.
        {anyUntagged &&
          ' Untagged lines are never sent to SmartLink; what you record is what gets invoiced.'}
      </p>

      {overPlanned &&
        (isDraft ? (
          <div className="notice notice-warn">
            <label>
              Shipping more than was ordered — a reason is required:{' '}
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={255} />
            </label>
          </div>
        ) : (
          <div className="notice notice-warn">
            Shipped more than was ordered — recorded reason:{' '}
            {plan.note ? <em>{plan.note}</em> : <em>none given</em>}
          </div>
        ))}

      <div className="toolbar">
        {recordable && (
          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={busy || !dirty}
            title={dirty ? undefined : 'Nothing has changed since the last save'}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        )}
        {isDraft && (
          <>
            <button
              type="button"
              className="primary"
              onClick={() => submit.mutate()}
              disabled={busy || totalPlanned === 0 || (overPlanned && !note.trim())}
            >
              Submit &amp; push to SmartLink
            </button>
            <button
              type="button"
              className="danger"
              onClick={() => cancel.mutate()}
              disabled={busy || cancel.isPending}
            >
              Cancel this dispatch
            </button>
          </>
        )}
      </div>

      {plan.status === 'submitted' && (
        <div className="toolbar">
          <button type="button" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
            Re-open for keying
          </button>
          <span className="keying-summary">
            Pulls this dispatch back to draft so cartons can be keyed. Cancels the SmartLink
            ground order first, and is refused once the picker has scanned anything.
          </span>
        </div>
      )}
    </>
  );
}
